import type { WatchInboundMessage, WatchPayload } from '../../hooks/useWatchSocket';

export type CommandFeedback = WatchPayload & { detail?: string };

export class WatchCommandError extends Error {
  readonly payload: CommandFeedback;

  constructor(message: string, payload: CommandFeedback) {
    super(message);
    this.name = 'WatchCommandError';
    this.payload = payload;
  }
}

const ERROR_LEVELS = new Set(['error', 'fatal']);

function extractFeedbackDetail(payload: WatchPayload): string | undefined {
  if (typeof payload.detail === 'string') {
    return payload.detail;
  }

  if (typeof payload.msg === 'string') {
    return payload.msg;
  }

  return undefined;
}

function isFailedFeedback(payload: WatchPayload): boolean {
  const isFailureByResult = typeof payload.result === 'string' && payload.result.toLowerCase() !== 'success';
  const isFailureBySuccess = payload.success === false;
  const isFailureByLevel = typeof payload.level === 'string' && ERROR_LEVELS.has(payload.level.toLowerCase());
  const isFailureBySeverity = typeof payload.severity === 'string' && ERROR_LEVELS.has(payload.severity.toLowerCase());
  const isFailureByStatus = typeof payload.status === 'number' && payload.status >= 400;
  // The watch's own reply code: 0 means success, anything else is a rejection (see mqtt-publisher.py).
  const isFailureByStatusCode = typeof payload.status_code === 'number' && payload.status_code !== 0;

  return (
    isFailureByResult ||
    isFailureBySuccess ||
    isFailureByLevel ||
    isFailureBySeverity ||
    isFailureByStatus ||
    isFailureByStatusCode
  );
}

/**
 * Returns the command feedback payload, or throws a `WatchCommandError` when the watch rejected the command.
 * The error message is the feedback detail, else `fallbackErrorMessage`, else empty.
 */
export function unwrapCommandFeedback(message: WatchInboundMessage, fallbackErrorMessage = ''): CommandFeedback {
  const payload: WatchPayload = message.room === 'feedback' ? message.payload : (message.payload as WatchPayload);
  const detail = extractFeedbackDetail(payload);
  const feedback: CommandFeedback = { ...payload, ...(detail !== undefined ? { detail } : {}) };

  if (isFailedFeedback(payload)) {
    throw new WatchCommandError(detail ?? fallbackErrorMessage, feedback);
  }

  return feedback;
}
